import { useHoneycombTheme } from '@honeycomb/ui-core';
import { useMemo } from 'react';
import { mapCMSBackgroundColorToThemeColor } from './mapCMSBackgroundColorToThemeColor';
import { mapCMSTextColorToThemeColor } from './mapCMSTextColorToThemeColor';
/**
 * Maps optional CMS-provided background and text color identifiers to Honeycomb theme tokens,
 * applying sensible fallbacks based on contrast.
 *
 * - If `backgroundColorCMS` is provided, it is mapped to a `ColorOption`.
 * - If `textColorCMS` is not provided and the background is dark, `'text.inverse'` is used as fallback.
 * - `headingColor` defaults to `textColor` if defined, otherwise `'text.heading'`.
 * - `isBackgroundDark` is a boolean indicating whether the resolved background is considered dark
 *   according to the theme.
 *
 * @param {ResolveCMSColorsParams} params
 * @returns {ResolvedColors} Object containing resolved `backgroundColor`, `textColor`, `headingColor`, and `isBackgroundDark`.
 */
export function useResolveCMSColors({ backgroundColorCMS, textColorCMS }) {
    const { theme } = useHoneycombTheme();
    return useMemo(() => {
        const backgroundColor = backgroundColorCMS ? mapCMSBackgroundColorToThemeColor(backgroundColorCMS) : undefined;
        const isBackgroundDark = theme.fn.isColorDark(backgroundColor || 'background.body');
        let textColor = textColorCMS ? mapCMSTextColorToThemeColor(textColorCMS) : undefined;
        if (!textColor && isBackgroundDark) {
            textColor = 'text.inverse';
        }
        return {
            backgroundColor,
            textColor,
            isBackgroundDark,
            headingColor: textColor || 'text.heading',
        };
    }, [backgroundColorCMS, textColorCMS, theme]);
}
